// Teams are just usergroups with specific `isTeams = true` attribute. This service is a wrapper to facilitate team related operations.
import { UserGroup } from "../../domain/UserGroup";
import { UserGroup as RestUserGroup } from "../../rest-model/UserGroup";
import { Collection } from "../../domain/Collection";
import { IIdentityRepository } from "../../repositories/IdentityRepository";
import { UserGroupAdapter } from "../UserGroupAdapter";
import { DEFAULT_PAGE_SIZE, GROUP_TYPES, PROPERTY_KEYS } from "../../constants/constants";

export interface ITeamsService {
    addTeam(newTeam: UserGroup): Promise<UserGroup>;
    deleteTeam(params: { teamId: string }): Promise<void>;
    getTeams(params: {
        emails?: string[];
        groupTypes?: string[];
        instanceIds?: string[];
        organizationId: string;
        page?: number;
        productIds?: string[];
        query?: string;
        size?: number;
    }): Promise<Collection<UserGroup>>;
    updateTeam(params: {
        description?: string;
        name?: string;
        team: UserGroup;
        usersToAdd?: string[];
        usersToRemove?: string[];
    }): Promise<RestUserGroup>;
}

export class TeamsService implements ITeamsService {
    identityRepository: IIdentityRepository;
    userGroupAdapter: UserGroupAdapter;

    constructor(identityRepository: IIdentityRepository) {
        this.identityRepository = identityRepository;
        this.userGroupAdapter = new UserGroupAdapter();
    }

    async deleteTeam(params: { teamId: string }): Promise<void> {
        return this.identityRepository.deleteUserGroup({ userGroupId: params.teamId });
    }

    // Fetch all teams (user groups with property isTeams = true)
    async getTeams({
        emails,
        groupTypes = [GROUP_TYPES.CUSTOM],
        instanceIds = [],
        organizationId,
        page = 1,
        size = DEFAULT_PAGE_SIZE,
        query = "",
        productIds = []
    }: {
        emails?: string[];
        groupTypes?: string[];
        instanceIds?: string[];
        organizationId: string;
        page?: number;
        size?: number;
        query?: string;
        productIds?: string[];
    }): Promise<Collection<UserGroup>> {
        const teamGroups = await this.identityRepository.getUserGroups({
            organizationId,
            size,
            page,
            groupTypes,
            includeTeams: true,
            properties: `properties[0].Name=${PROPERTY_KEYS.TEAM}&properties[0].Value=true`,
            query,
            productIds,
            instanceIds,
            ...(emails !== undefined ? { emails } : {})
        });

        return new Collection<UserGroup>({
            items: teamGroups.items?.map((userGroup) => this.userGroupAdapter.adaptToDomain(userGroup)),
            totalCount: teamGroups.totalCount
        });
    }

    async addTeam(newTeam: UserGroup): Promise<UserGroup> {
        const response = await this.identityRepository.addUserGroup(this.userGroupAdapter.adaptToRest(newTeam));
        return this.userGroupAdapter.adaptToDomain(response);
    }

    async updateTeam({
        description,
        name,
        team,
        usersToAdd = [],
        usersToRemove = []
    }: {
        description?: string;
        name?: string;
        team: UserGroup;
        usersToAdd?: string[];
        usersToRemove?: string[];
    }): Promise<RestUserGroup> {
        if (usersToAdd.length > 0) {
            await this.identityRepository.addUserGroupUsers({
                userGroupId: team.id,
                userEmails: usersToAdd
            });
        }

        if (usersToRemove.length > 0) {
            const removeResult = await this.identityRepository.deleteUserGroupUsers({
                userGroupId: team.id,
                userEmails: usersToRemove
            });
            const allSucceeded = Array.isArray(removeResult) && removeResult.every((success: boolean) => success);
            if (!allSucceeded) {
                if (Array.isArray(removeResult) && removeResult.length === usersToRemove.length) {
                    const failedUsers = usersToRemove.filter((_, index) => !removeResult[index]);
                    throw new Error(`Failed to remove the following users from the team: ${failedUsers.join(", ")}`);
                }
                throw new Error("Failed to remove one or more users from the team due to an unexpected error.");
            }
        }
        const patchUpdates: { [key: string]: string } = {};
        if (description !== undefined) {
            patchUpdates.description = description;
        }
        if (name) {
            patchUpdates.name = name;
        }

        if (Object.keys(patchUpdates).length > 0) {
            const restGroup = this.userGroupAdapter.adaptToRest({ ...team, ...patchUpdates });
            await this.identityRepository.updateUserGroup(restGroup);
        }
        // return the updated group
        return await this.identityRepository.getUserGroup({ id: team.id });
    }
}
